import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronUp,
  Circle,
  Clock,
  Loader2,
  Mic,
  MicOff,
  Paperclip,
  Pause,
  Play,
  Square,
  X,
} from 'lucide-react';

import { AudioLevelBars } from '@/components/demo-studio/AudioLevelBars';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService, { type DemoStudioSession } from '@/lib/api/demoStudio';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAudioDevices } from '@/hooks/useAudioDevices';
import { useAudioLevel } from '@/hooks/useAudioLevel';
import { useAudioRecording } from '@/hooks/useAudioRecording';

const ACTIVE_STATUSES = new Set(['launching', 'active', 'processing']);
const SESSION_POLL_INTERVAL_MS = 15_000;
const launchStorageKey = (sessionId: string) => `demo-studio-launch:${sessionId}`;
const HEADFUL_LIVE_VIEW_RATIO = 16 / 9;
const FULLSCREEN_LIVE_VIEW_RATIO = 1.9;
const FULLSCREEN_LIVE_VIEW_ZOOM = 1.05;

const formatDuration = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  }

  return [minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};

const DemoStudioSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [stopping, setStopping] = React.useState(false);
  const [stopRequested, setStopRequested] = React.useState(false);
  const [stopRequestedAtMs, setStopRequestedAtMs] = React.useState<number | null>(null);
  const [session, setSession] = React.useState<DemoStudioSession | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);
  const { micStatus, startRecording, stopRecording } = useAudioRecording();

  // Capture the `source=knowledge_gap&summary_id=...` query params once on
  // mount. Stashed on refs so the post-stop redirect logic can read them
  // without causing re-renders, and so they survive route changes within
  // the session page lifecycle.
  const launchSourceRef = React.useRef<string | null>(null);
  const launchSummaryIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    launchSourceRef.current = params.get('source');
    launchSummaryIdRef.current = params.get('summary_id');
  }, []);
  const { inputDevices } = useAudioDevices();
  const [selectedDeviceId, setSelectedDeviceId] = React.useState<string | undefined>(undefined);
  const { audioLevel, micConnected, stream: monitorStreamRef } = useAudioLevel(selectedDeviceId);
  const [muted, setMuted] = React.useState(false);
  const [isPaused, setIsPaused] = React.useState(false);
  const [pauseActionPending, setPauseActionPending] = React.useState(false);
  const audioSegmentIndexRef = React.useRef(0);

  const getRecordingControls = React.useCallback(
    (s: DemoStudioSession | null) => {
      if (!s?.planned_walkthrough_json) return null;
      const pwj = s.planned_walkthrough_json as Record<string, unknown>;
      return (pwj.recording_controls as Record<string, unknown>) ?? null;
    },
    [],
  );

  // --- File state (files are seeded from the pre-launch screen) ---
  const [uploadedFiles, setUploadedFiles] = React.useState<string[]>([]);
  const [fileChooserPending, setFileChooserPending] = React.useState(false);
  const [fileChooserMode, setFileChooserMode] = React.useState<string | null>(null);
  // Track whether the user has dismissed the current file chooser overlay so
  // the 500ms poll doesn't immediately re-show it.  Reset when a genuinely new
  // file chooser event arrives (pending transitions false → true).
  const fileChooserDismissedRef = React.useRef(false);
  const prevPendingRef = React.useRef(false);

  const toggleMute = React.useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      // Mute/unmute all audio tracks on the monitoring stream
      monitorStreamRef.current?.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
      return next;
    });
  }, [monitorStreamRef]);

  // Re-apply mute state when the monitoring stream reconnects (e.g. after device switch)
  React.useEffect(() => {
    if (muted && monitorStreamRef.current) {
      monitorStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = false;
      });
    }
  }, [micConnected, muted, monitorStreamRef]);

  const handleResolveFileChooser = React.useCallback(
    async (filenames: string[]) => {
      if (!session?.id) return;
      const response = await demoStudioService.resolveFileChooser(session.id, filenames);
      if (!response.success) {
        toast({
          title: 'Failed to attach file',
          description: response.error || 'Could not attach the file to the browser input.',
          variant: 'destructive',
        });
        return;
      }
      setFileChooserPending(false);
      setFileChooserMode(null);
    },
    [session?.id, toast],
  );

  const handleCancelFileChooser = React.useCallback(async () => {
    if (!session?.id) return;
    // Mark as dismissed so the poll doesn't re-show the overlay.
    fileChooserDismissedRef.current = true;
    setFileChooserPending(false);
    setFileChooserMode(null);
    const response = await demoStudioService.cancelFileChooser(session.id);
    if (!response.success) {
      toast({
        title: "Couldn't dismiss file picker",
        description: response.error || 'The browser may still be waiting for a file.',
        variant: 'destructive',
      });
    }
  }, [session?.id, toast]);

  const sessionStatus = session?.status;
  const isActive = Boolean(session && ACTIVE_STATUSES.has(session.status));

  // Poll for file chooser status while session is active.
  // Only poll when files have been uploaded — there's nothing to resolve
  // if no files were seeded, and this avoids noisy CloudWatch logs.
  const hasUploadedFiles = uploadedFiles.length > 0;
  React.useEffect(() => {
    if (!sessionId || !isActive || !hasUploadedFiles) return;

    const poll = async () => {
      try {
        const response = await demoStudioService.getFileChooserStatus(sessionId);
        if (response.success && response.data) {
          const nowPending = response.data.pending;

          // Reset the dismissed flag when a genuinely NEW file chooser opens
          // (the backend cleared the old one and a new one arrived).
          if (nowPending && !prevPendingRef.current) {
            fileChooserDismissedRef.current = false;
          }
          prevPendingRef.current = nowPending;

          // Only update state if the user hasn't dismissed this round.
          if (!fileChooserDismissedRef.current) {
            setFileChooserPending(nowPending);
            setFileChooserMode(response.data.mode ?? null);
          }
        }
      } catch {
        // Swallow polling errors
      }
    };

    const interval = window.setInterval(poll, 2000);
    return () => window.clearInterval(interval);
  }, [sessionId, isActive, hasUploadedFiles]);

  // Hydrate the list of files seeded at launch time so the picker overlay
  // can list them when the remote browser opens a file dialog.
  React.useEffect(() => {
    if (!sessionId || !isActive) return;
    let cancelled = false;
    (async () => {
      const response = await demoStudioService.getUploadedFiles(sessionId);
      if (!cancelled && response.success && response.data) {
        setUploadedFiles(response.data.files ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, isActive]);

  const timingMarksRef = React.useRef({ visible: false, narration_start: false });
  const pageOpenedAtRef = React.useRef(Date.now());

  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      rootOverflow: root?.style.overflow ?? '',
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (root) {
      root.style.overflow = 'hidden';
    }

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      if (root) {
        root.style.overflow = previous.rootOverflow;
      }
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // If there is no launch context in sessionStorage this is a
        // reconnect (the user navigated back to an existing session)
        // rather than a fresh launch, so we call the reconnect endpoint
        // to re-establish the backend CDP capture connection.
        const isFreshLaunch = Boolean(sessionStorage.getItem(launchStorageKey(sessionId)));
        const response = isFreshLaunch
          ? await demoStudioService.getSession(sessionId)
          : await demoStudioService.reconnectSession(sessionId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load Studio session.');
        }

        if (!cancelled) {
          setSession(response.data);

          // Initialize audio segment index from existing segments
          const controls = response.data.planned_walkthrough_json
            ? (response.data.planned_walkthrough_json as Record<string, unknown>)
                .recording_controls as Record<string, unknown> | undefined
            : undefined;
          const existingSegments = (controls?.audio_segments as Array<unknown>) ?? [];
          audioSegmentIndexRef.current = existingSegments.length;

          // C1: Detect pause state on reconnect
          if (
            controls?.active_pause_start_ms != null &&
            response.data.narration_start_ms != null
          ) {
            setIsPaused(true);
          }
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Unable to load session',
            description: error instanceof Error ? error.message : 'Failed to load Studio session.',
            variant: 'destructive',
          });
          navigate('/demo-studio', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSession();

    return () => {
      cancelled = true;
    };
  }, [navigate, sessionId, toast]);

  // Clean up the launch context from sessionStorage once the session page has loaded
  React.useEffect(() => {
    if (sessionId) {
      sessionStorage.removeItem(launchStorageKey(sessionId));
    }
  }, [sessionId]);

  // Send keepalive pings while paused to prevent Anchor idle timeout.
  React.useEffect(() => {
    if (!sessionId || !isPaused || !isActive) return;

    const KEEPALIVE_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
    const interval = window.setInterval(async () => {
      try {
        await demoStudioService.keepaliveSession(sessionId);
      } catch {
        // Best-effort — swallow errors silently
      }
    }, KEEPALIVE_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [sessionId, isPaused, isActive]);

  // Poll for session status changes so we detect server-side abandonment.
  // We track the status string separately so the interval doesn't reset on
  // every poll response (which would produce a new session object each time).
  const abandonedAudioFlushedRef = React.useRef(false);
  React.useEffect(() => {
    if (!sessionId || !sessionStatus || !ACTIVE_STATUSES.has(sessionStatus) || stopRequested) return;

    const interval = window.setInterval(async () => {
      try {
        const response = await demoStudioService.getSession(sessionId);
        if (response.success && response.data) {
          setSession(response.data);

          // When the backend marks the session as abandoned (timeout / CDP
          // disconnect), flush any in-progress audio recording and upload it
          // so the narration is not lost.
          if (
            response.data.status === 'abandoned' &&
            !abandonedAudioFlushedRef.current
          ) {
            abandonedAudioFlushedRef.current = true;
            if (micStatus === 'active' || isPaused) {
              try {
                const narrationBlob = await stopRecording();
                if (narrationBlob && narrationBlob.size > 0) {
                  const file = new File([narrationBlob], 'narration.webm', {
                    type: narrationBlob.type || 'audio/webm',
                  });
                  await demoStudioService.uploadAudio(sessionId, file, audioSegmentIndexRef.current);
                }
              } catch {
                // Best-effort — the backend will still recover the video without audio
              }
            }
          }
        }
      } catch {
        // Swallow polling errors silently
      }
    }, SESSION_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [sessionId, sessionStatus, stopRequested, micStatus, isPaused, stopRecording]);

  React.useEffect(() => {
    timingMarksRef.current = {
      visible: Boolean(session?.visible_start_ms != null),
      narration_start: Boolean(session?.narration_start_ms != null),
    };
  }, [session?.id, session?.visible_start_ms, session?.narration_start_ms]);

  React.useEffect(() => {
    if (!session?.created_at || session?.narration_start_ms == null) {
      setElapsedSeconds(0);
      return;
    }

    const startMs = new Date(session.created_at).getTime() + session.narration_start_ms;
    const endMs = session.completed_at
      ? new Date(session.completed_at).getTime()
      : stopRequestedAtMs;

    // Calculate total paused duration from recording controls
    const controls = getRecordingControls(session);
    const pauseIntervals = (controls?.pause_intervals_ms as Array<{ start_ms: number; end_ms: number }>) ?? [];
    const completedPausedMs = pauseIntervals.reduce(
      (sum, interval) => sum + Math.max(0, interval.end_ms - interval.start_ms),
      0,
    );
    const activePauseStartMs = controls?.active_pause_start_ms as number | undefined;

    const updateElapsed = () => {
      const referenceMs = endMs ?? Date.now();
      const rawElapsed = referenceMs - startMs;

      // Subtract completed pause intervals
      let totalPausedMs = completedPausedMs;

      // If currently paused, also subtract the active pause duration
      if (activePauseStartMs != null) {
        const sessionCreatedMs = new Date(session.created_at).getTime();
        const activePauseAbsoluteMs = sessionCreatedMs + activePauseStartMs;
        totalPausedMs += Math.max(0, referenceMs - activePauseAbsoluteMs);
      }

      setElapsedSeconds(Math.max(0, (rawElapsed - totalPausedMs) / 1000));
    };

    updateElapsed();

    if (endMs) {
      return;
    }

    const interval = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(interval);
  }, [session?.created_at, session?.completed_at, session?.narration_start_ms, stopRequestedAtMs, session?.planned_walkthrough_json, getRecordingControls]);

  const markTimingEvent = React.useCallback(
    async (eventType: 'visible' | 'narration_start', overrideElapsedMs?: number) => {
      if (!session?.id || timingMarksRef.current[eventType]) {
        return;
      }

      // Compute elapsed ms since session creation (not page open) so the
      // stored offset is consistent with created_at, which the UI timer
      // relies on.  Falls back to page-open time when created_at is
      // unavailable (shouldn't happen in practice).
      // An override can be supplied when the caller has already captured
      // the timestamp at the exact moment the event occurred (e.g.
      // narration_start is captured before the async startRecording call
      // to avoid inflating the offset with MediaRecorder setup time).
      const referenceMs = session?.created_at
        ? new Date(session.created_at).getTime()
        : pageOpenedAtRef.current;
      const clientElapsedMs = overrideElapsedMs ?? Math.max(0, Date.now() - referenceMs);

      // Retry up to 2 times for the 'visible' event since a single failure
      // here causes the audio/video sync offset to fall back to a less
      // accurate value.
      const maxAttempts = eventType === 'visible' ? 3 : 1;
      let lastError: Error | null = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const response = await demoStudioService.markTimingEvent(
            session.id,
            eventType,
            clientElapsedMs,
          );
          if (!response.success || !response.data) {
            throw new Error(response.error || `Failed to mark ${eventType} timing.`);
          }
          timingMarksRef.current[eventType] = true;
          setSession(response.data);
          return;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          if (attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
          }
        }
      }
      if (lastError) throw lastError;
    },
    [session?.id, session?.created_at],
  );

  // Fallback: if the iframe onLoad never fires (or all retries fail), attempt
  // to mark the 'visible' event after a generous timeout so the backend at
  // least has a timing marker to work with.
  React.useEffect(() => {
    if (!session?.id || timingMarksRef.current.visible) return;

    const VISIBLE_FALLBACK_MS = 15_000;
    const timer = window.setTimeout(() => {
      if (timingMarksRef.current.visible) return;
      void markTimingEvent('visible').catch(() => undefined);
    }, VISIBLE_FALLBACK_MS);

    return () => window.clearTimeout(timer);
  }, [session?.id, markTimingEvent]);

  const handleStartNarration = React.useCallback(async () => {
    // Capture the elapsed time BEFORE starting the recorder so the
    // narration_start_ms offset isn't inflated by MediaRecorder setup
    // time or getUserMedia latency.
    const referenceMs = session?.created_at
      ? new Date(session.created_at).getTime()
      : pageOpenedAtRef.current;
    const narrationElapsedMs = Math.max(0, Date.now() - referenceMs);

    const status = await startRecording(monitorStreamRef.current, selectedDeviceId);
    if (status === 'denied') {
      toast({
        title: 'Microphone access denied',
        description: 'Allow microphone access in your browser to record narration.',
        variant: 'destructive',
      });
      return;
    }
    if (status === 'error') {
      toast({
        title: 'Microphone unavailable',
        description: 'Studio could not start microphone recording.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await markTimingEvent('narration_start', narrationElapsedMs);
    } catch (error) {
      toast({
        title: 'Timing sync unavailable',
        description: error instanceof Error ? error.message : 'Failed to mark narration start timing.',
        variant: 'destructive',
      });
      return;
    }

  }, [markTimingEvent, monitorStreamRef, selectedDeviceId, session?.created_at, startRecording, toast]);

  const handlePause = React.useCallback(async () => {
    if (!session?.id || pauseActionPending) return;

    // Capture elapsed at the exact moment the user clicks Pause
    const elapsedMs = Date.now() - new Date(session.created_at).getTime();
    const previousSession = session; // snapshot for rollback

    setPauseActionPending(true);

    // Optimistic updates — UI reflects paused state immediately so the user
    // doesn't see the timer keep ticking while the audio upload + pause
    // endpoint round-trip complete. The backend records the same
    // `active_pause_start_ms` via `client_elapsed_ms`, so the authoritative
    // response will match what we patched locally.
    setIsPaused(true);
    setSession((prev) => {
      if (!prev) return prev;
      const pwj = (prev.planned_walkthrough_json ?? {}) as Record<string, unknown>;
      const controls = (pwj.recording_controls as Record<string, unknown>) ?? {};
      return {
        ...prev,
        planned_walkthrough_json: {
          ...pwj,
          recording_controls: {
            ...controls,
            active_pause_start_ms: elapsedMs,
          },
        },
      };
    });

    const rollback = async () => {
      setIsPaused(false);
      setSession(previousSession);
      // Restart recording since stopRecording() may have already killed the MediaRecorder
      const micResult = await startRecording(monitorStreamRef.current, selectedDeviceId);
      if (micResult !== 'active') {
        toast({
          title: 'Microphone recovery failed',
          description: 'Could not restart the microphone after pause failed. Click Record to try again.',
          variant: 'destructive',
        });
      }
    };

    try {
      // Flush current audio segment to S3 before pausing
      const narrationBlob = await stopRecording();
      if (narrationBlob && narrationBlob.size > 0) {
        try {
          const file = new File([narrationBlob], 'narration.webm', {
            type: narrationBlob.type || 'audio/webm',
          });
          await demoStudioService.uploadAudio(session.id, file, audioSegmentIndexRef.current);
          audioSegmentIndexRef.current += 1;
        } catch {
          // Best-effort — segment may be lost but pause should still proceed
        }
      }

      const response = await demoStudioService.pauseSession(session.id, elapsedMs);
      if (!response.success || !response.data) {
        await rollback();
        toast({
          title: 'Failed to pause',
          description: response.error || 'Could not pause the recording.',
          variant: 'destructive',
        });
        return;
      }
      // Authoritative server response overrides our optimistic patch
      setSession(response.data);
    } catch (error) {
      await rollback();
      toast({
        title: 'Failed to pause',
        description: error instanceof Error ? error.message : 'Could not pause the recording.',
        variant: 'destructive',
      });
    } finally {
      setPauseActionPending(false);
    }
  }, [session, pauseActionPending, startRecording, stopRecording, monitorStreamRef, selectedDeviceId, toast]);

  const handleResume = React.useCallback(async () => {
    if (!session?.id || pauseActionPending) return;

    const previousSession = session; // snapshot for rollback
    setPauseActionPending(true);

    try {
      // Start mic FIRST so the timestamp matches actual audio start
      const micResult = await startRecording(monitorStreamRef.current, selectedDeviceId);
      if (micResult !== 'active') {
        toast({
          title: 'Microphone unavailable',
          description: 'Could not restart narration.',
          variant: 'destructive',
        });
        setPauseActionPending(false);
        return; // Don't resume backend if mic failed
      }

      // Capture elapsed AFTER mic is running — this is the actual audio start
      const elapsedMs = Date.now() - new Date(session.created_at).getTime();

      // Optimistic updates — UI reflects resumed state immediately. Close the
      // active pause by appending it to pause_intervals_ms so the timer
      // continues to account for it while we wait for the authoritative
      // response.
      setIsPaused(false);
      setSession((prev) => {
        if (!prev) return prev;
        const pwj = (prev.planned_walkthrough_json ?? {}) as Record<string, unknown>;
        const controls = (pwj.recording_controls as Record<string, unknown>) ?? {};
        const activeStart = controls.active_pause_start_ms as number | undefined;
        const intervals =
          (controls.pause_intervals_ms as Array<{ start_ms: number; end_ms: number }>) ?? [];
        const nextIntervals =
          activeStart != null
            ? [...intervals, { start_ms: activeStart, end_ms: elapsedMs }]
            : intervals;
        const nextControls: Record<string, unknown> = { ...controls };
        delete nextControls.active_pause_start_ms;
        nextControls.pause_intervals_ms = nextIntervals;
        return {
          ...prev,
          planned_walkthrough_json: {
            ...pwj,
            recording_controls: nextControls,
          },
        };
      });

      const rollback = async () => {
        setIsPaused(true);
        setSession(previousSession);
        await stopRecording();
      };

      const response = await demoStudioService.resumeSession(session.id, elapsedMs);
      if (!response.success || !response.data) {
        await rollback();
        toast({
          title: 'Failed to resume',
          description: response.error || 'Could not resume the recording.',
          variant: 'destructive',
        });
        return;
      }
      // Authoritative server response overrides our optimistic patch
      setSession(response.data);
    } catch (error) {
      // Rollback optimistic UI + mic. previousSession restores the pre-resume
      // state (including active_pause_start_ms if it was set).
      setIsPaused(true);
      setSession(previousSession);
      await stopRecording();
      toast({
        title: 'Failed to resume',
        description: error instanceof Error ? error.message : 'Could not resume the recording.',
        variant: 'destructive',
      });
    } finally {
      setPauseActionPending(false);
    }
  }, [session, pauseActionPending, startRecording, stopRecording, monitorStreamRef, selectedDeviceId, toast]);

  const handleStop = React.useCallback(async () => {
    if (!session?.id) return;

    // Capture elapsed at the exact moment the user clicks Stop
    const elapsedMs = Date.now() - new Date(session.created_at).getTime();
    const wasPaused = isPaused;

    setStopping(true);
    setStopRequested(true);
    setStopRequestedAtMs(Date.now());
    setIsPaused(false);
    try {
      let narrationBlob: Blob | null = null;
      if (micStatus === 'active' || isPaused) {
        narrationBlob = await stopRecording();
      }

      // Upload narration audio BEFORE stopping so that raw_audio_path is set
      // on the DemoRecordingSession row before _finalize_stopped_session runs.
      if (narrationBlob && narrationBlob.size > 0) {
        try {
          const file = new File([narrationBlob], 'narration.webm', {
            type: narrationBlob.type || 'audio/webm',
          });
          const uploadResponse = await demoStudioService.uploadAudio(session.id, file, audioSegmentIndexRef.current);
          if (!uploadResponse.success || !uploadResponse.data) {
            throw new Error(uploadResponse.error || 'Failed to upload narration.');
          }
        } catch (uploadError) {
          toast({
            title: 'Narration upload failed',
            description: uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload narration.',
            variant: 'destructive',
          });
          // Proceed with stop anyway – the audio is already in storage even if
          // the DB update failed, or the user may want to stop regardless.
        }
      }

      const stopResponse = await demoStudioService.stopSession(session.id, elapsedMs);

      if (!stopResponse.success || !stopResponse.data) {
        throw new Error(stopResponse.error || 'Failed to stop Studio session.');
      }

      toast({
        title: 'Session stopped',
        description: 'The recording is being finalized in the background.',
      });
      // Close-back behavior for knowledge-gap-launched captures: try to
      // close the studio tab (which was opened via window.open from the
      // gap detail panel) and fall back to redirecting to the gap. For
      // user-initiated captures from /demo-studio, keep today's redirect.
      if (
        launchSourceRef.current === 'knowledge_gap' &&
        launchSummaryIdRef.current
      ) {
        if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
          try {
            window.close();
            return;
          } catch {
            // window.close() blocked or threw — fall through to redirect.
          }
        }
        navigate(`/agent-knowledge?tab=gaps&selected=${encodeURIComponent(launchSummaryIdRef.current)}`, {
          replace: true,
        });
        return;
      }
      navigate('/demo-studio', { replace: true });
    } catch (error) {
      setStopRequested(false);
      setStopRequestedAtMs(null);
      if (wasPaused) setIsPaused(true);
      toast({
        title: 'Stop failed',
        description: error instanceof Error ? error.message : 'Failed to stop Studio session.',
        variant: 'destructive',
      });
    } finally {
      setStopping(false);
    }
  }, [micStatus, isPaused, navigate, session?.id, session?.created_at, stopRecording, toast]);

  const liveViewUrl = session?.fullscreen_live_view_url || session?.live_view_url || null;
  const usingFullscreenLiveView = Boolean(session?.fullscreen_live_view_url);
  const liveViewRatio = usingFullscreenLiveView ? FULLSCREEN_LIVE_VIEW_RATIO : HEADFUL_LIVE_VIEW_RATIO;
  const liveViewZoom = usingFullscreenLiveView ? FULLSCREEN_LIVE_VIEW_ZOOM : 1;
  const controlsDisabled = loading || stopping || !isActive;
  const showFinishingState = stopRequested;
  return (
    <>
      <Helmet>
        <title>Studio Session | Hobbes</title>
      </Helmet>

      <div className="h-screen overflow-hidden bg-[#191919] text-white">
        <div className="flex h-screen flex-col">
          <main className="flex flex-1 flex-col overflow-hidden px-3 pb-3 pt-3">
            <div className="flex flex-1 min-h-0">
              <section className="flex min-w-0 flex-1 flex-col items-center justify-center relative">
                <div
                  className="flex flex-col overflow-hidden rounded-2xl border border-[#2b2b2b] bg-[#101010] shadow-[0_8px_20px_rgba(0,0,0,0.05)]"
                  style={{
                    width: `min(calc((100vh - 132px) * ${liveViewRatio}), calc(100vw - 32px))`,
                  }}
                >
                  <div className="relative w-full overflow-hidden bg-[#0f0f0f]">
                    <AspectRatio ratio={liveViewRatio} className="w-full bg-[#0f0f0f]">
                      {loading ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#111111]">
                          <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading workspace...
                          </div>
                        </div>
                      ) : showFinishingState ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#101010] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="text-white">
                                Finishing recording
                              </CardTitle>
                              <CardDescription className="text-white/70">
                                We are closing the managed browser and returning you to Studio.
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="flex items-center gap-3">
                              <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Stopping recording...
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ) : session?.status === 'abandoned' ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#101010] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-white">
                                <Clock className="h-5 w-5 text-zinc-400" />
                                Session expired
                              </CardTitle>
                              <CardDescription className="text-white/70">
                                The managed browser was closed due to inactivity. Return to Studio to start a new recording.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Button onClick={() => navigate('/demo-studio')}>Back to Studio</Button>
                            </CardContent>
                          </Card>
                        </div>
                      ) : liveViewUrl && isActive ? (
                        <div className="h-full w-full overflow-hidden bg-[#0f0f0f]">
                          <iframe
                            src={liveViewUrl}
                            title="Studio Live View"
                            className="block h-full w-full origin-center border-0 bg-[#0f0f0f]"
                            style={{
                              transform: `scale(${liveViewZoom})`,
                              width: `${100 / liveViewZoom}%`,
                              height: `${100 / liveViewZoom}%`,
                              marginLeft: `${((100 / liveViewZoom) - 100) / -2}%`,
                              marginTop: `${((100 / liveViewZoom) - 100) / -2}%`,
                            }}
                            allow="clipboard-read; clipboard-write"
                            onLoad={() => {
                              void markTimingEvent('visible').catch(() => {
                                // Retries exhausted — logged on backend; the
                                // fallback timer below will make one more attempt.
                              });
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-[#101010] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="text-white">Session unavailable</CardTitle>
                              <CardDescription className="text-white/70">
                                The live workspace is no longer active. Return to Studio to launch a new session.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Button onClick={() => navigate('/demo-studio')}>Back to Studio</Button>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </AspectRatio>
                  </div>
                </div>
              </section>

            </div>

            {/* File chooser picker overlay */}
            {fileChooserPending && uploadedFiles.length > 0 && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#1a1a1a] p-4 shadow-2xl">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white">
                      Select file{fileChooserMode === 'selectMultiple' ? 's' : ''} for input
                    </h3>
                    <button
                      type="button"
                      onClick={() => void handleCancelFileChooser()}
                      className="rounded-md p-1 text-white/40 transition hover:bg-white/10 hover:text-white/80"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex flex-col gap-1">
                    {uploadedFiles.map((name, index) => (
                      <button
                        key={`${name}-${index}`}
                        type="button"
                        onClick={() => void handleResolveFileChooser([name])}
                        className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-[13px] text-white/80 transition hover:bg-white/[0.06]"
                      >
                        <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-white/40" />
                        <span className="truncate">{name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* File chooser pending but no files uploaded */}
            {fileChooserPending && uploadedFiles.length === 0 && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#1a1a1a] p-4 shadow-2xl">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white">No attachments available</h3>
                    <button
                      type="button"
                      onClick={() => void handleCancelFileChooser()}
                      className="rounded-md p-1 text-white/40 transition hover:bg-white/10 hover:text-white/80"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mb-3 text-[13px] text-white/50">
                    This recording didn't include any attachments. Stop, then start a new recording with files attached on the Studio screen.
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleCancelFileChooser()}
                    className="w-full rounded-lg bg-white/[0.06] px-3 py-2 text-[13px] text-white/70 transition hover:bg-white/[0.1]"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center px-4 pb-1 pt-3">
              <div className="flex h-11 items-center rounded-full border border-white/[0.08] bg-white/[0.03] text-[13px] font-medium tracking-[-0.1px] text-white/90 backdrop-blur-sm">
                {/* Timer */}
                <div className="flex items-center gap-2 pl-4 pr-3.5">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      isPaused
                        ? 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.5)]'
                        : session?.narration_start_ms != null
                          ? 'bg-[#c03740] shadow-[0_0_6px_rgba(192,55,64,0.5)]'
                          : 'bg-white/20'
                    }`}
                  />
                  <span className="tabular-nums text-white/70">{formatDuration(elapsedSeconds)}</span>
                </div>

                <div className="h-4 w-px bg-white/[0.08]" />

                {/* Mic mute toggle */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className={`flex items-center justify-center px-3 transition-colors ${
                    muted ? 'text-red-400/90 hover:text-red-300' : 'text-white/60 hover:text-white/90'
                  }`}
                  aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
                >
                  {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>

                {/* Audio level bars — always live when mic connected */}
                {micConnected && (
                  <div className="flex items-center pr-1">
                    <AudioLevelBars audioLevel={muted ? 0 : audioLevel} />
                  </div>
                )}

                {/* Device selector (only when multiple mics) */}
                {inputDevices.length > 1 && (
                  <>
                    <div className="h-4 w-px bg-white/[0.08]" />
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          disabled={controlsDisabled || micStatus === 'active' || isPaused}
                          className="flex items-center gap-1.5 px-3 text-white/50 transition-colors hover:text-white/80 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <span className="max-w-[120px] truncate text-[12px]">
                            {inputDevices.find((d) => d.deviceId === selectedDeviceId)?.label ??
                              inputDevices[0]?.label ?? 'Mic'}
                          </span>
                          <ChevronUp className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        align="center"
                        sideOffset={8}
                        className="w-72 border-white/[0.08] bg-[#1a1a1a] p-1 shadow-xl"
                      >
                        {inputDevices.map((device) => (
                          <button
                            key={device.deviceId}
                            type="button"
                            onClick={() => setSelectedDeviceId(device.deviceId)}
                            className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] text-white/80 transition hover:bg-white/[0.06] ${
                              (selectedDeviceId === device.deviceId ||
                                (!selectedDeviceId && device.deviceId === inputDevices[0]?.deviceId))
                                ? 'bg-white/[0.04] text-white'
                                : ''
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                                (selectedDeviceId === device.deviceId ||
                                  (!selectedDeviceId && device.deviceId === inputDevices[0]?.deviceId))
                                  ? 'bg-[#60A5FA]'
                                  : 'bg-transparent'
                              }`}
                            />
                            <span className="truncate">{device.label}</span>
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                  </>
                )}

                {/* Pause / Resume (only when recording is active or paused) */}
                {(micStatus === 'active' || isPaused) && (
                  <>
                    <div className="h-4 w-px bg-white/[0.08]" />
                    <button
                      type="button"
                      onClick={isPaused ? handleResume : handlePause}
                      disabled={controlsDisabled || pauseActionPending}
                      className={`flex items-center gap-2 px-3 transition disabled:cursor-not-allowed disabled:opacity-40 ${
                        isPaused
                          ? 'text-amber-400 hover:text-amber-300'
                          : 'text-white/60 hover:text-white/90'
                      }`}
                      aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
                    >
                      {isPaused ? (
                        <Play className="h-3.5 w-3.5 fill-current" />
                      ) : (
                        <Pause className="h-3.5 w-3.5 fill-current" />
                      )}
                    </button>
                  </>
                )}

                <div className="h-4 w-px bg-white/[0.08]" />

                {/* Record / Stop */}
                <button
                  type="button"
                  onClick={micStatus === 'active' || isPaused ? handleStop : () => void handleStartNarration()}
                  disabled={controlsDisabled}
                  className={`flex items-center gap-2 rounded-r-full pl-3.5 pr-4 transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    micStatus === 'active' || isPaused
                      ? 'bg-[#c03740]/20 text-[#f87171] hover:bg-[#c03740]/30'
                      : 'text-white/70 hover:bg-white/[0.04] hover:text-white'
                  }`}
                  style={{ height: '100%' }}
                  aria-label={micStatus === 'active' || isPaused ? 'Stop recording' : 'Start recording'}
                >
                  {stopping ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Stopping</span>
                    </>
                  ) : micStatus === 'active' || isPaused ? (
                    <>
                      <Square className="h-3 w-3 fill-current" />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <Circle className="h-3.5 w-3.5 fill-[#c03740] text-[#c03740]" />
                      <span>Record</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default DemoStudioSessionPage;
